export function ClosingProcess() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Navigating the Closing Process
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The closing process is the final hurdle in selling your apartment building. Understanding what happens 
            during closing and how to prepare is among the most practical tips for selling an apartment building 
            successfully. Proper preparation ensures smooth closings and prevents last-minute complications that 
            could delay or derail your sale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Pre-Closing Preparation</h3>
            <p className="text-gray-600 mb-6">
              The weeks leading up to closing require careful coordination and preparation. Missing documents, 
              unresolved issues, or poor communication can delay closings and create unnecessary stress. These 
              preparation steps ensure you're ready for a smooth closing day.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-file-list-3-line text-green-600 mr-2"></i>
                  Finalize All Documentation
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  Work with your attorney and title company to ensure all required documents are prepared and 
                  reviewed well before closing. Last-minute document issues are a common cause of closing delays.
                </p>
                <ul className="text-gray-600 text-xs space-y-1">
                  <li>• Deed and transfer documents</li>
                  <li>• Bill of sale for personal property</li>
                  <li>• Lease assignment agreements</li>
                  <li>• Tenant estoppel certificates</li>
                  <li>• Closing statement and prorations</li>
                  <li>• Affidavits and disclosures</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-money-dollar-circle-line text-orange-600 mr-2"></i>
                  Review Closing Statement
                </h4>
                <p className="text-gray-600 text-sm">
                  Carefully review the closing statement (HUD-1 or settlement statement) several days before 
                  closing. Verify all numbers including purchase price, prorations, credits, and fees. Question 
                  any items you don't understand or that seem incorrect. Errors caught early are easily fixed; 
                  errors discovered at closing cause delays.
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-key-line text-red-600 mr-2"></i>
                  Prepare for Property Transfer
                </h4>
                <p className="text-gray-600 text-sm">
                  Organize all keys, access codes, manuals, warranties, and vendor contact information for 
                  transfer to the buyer. Create a comprehensive property transfer package that includes 
                  everything the new owner needs to operate the property smoothly from day one.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/21403ac3a9c22f563ac10a085eb53077.jpg"
              alt="Real estate closing"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Understanding Closing Costs and Prorations
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Knowing what to expect for closing costs is among the essential tips for selling an apartment building. 
            These costs can total 2-4% of the sale price, so understanding and budgeting for them prevents surprises. 
            Additionally, understanding prorations ensures you receive proper credits for prepaid expenses.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-wallet-line text-orange-600 mr-2"></i>
                Typical Seller Closing Costs
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-start pb-3 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Real Estate Commission</p>
                    <p className="text-xs text-gray-500">If using a broker</p>
                  </div>
                  <p className="text-sm text-gray-900">4-6%</p>
                </div>
                <div className="flex justify-between items-start pb-3 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Attorney Fees</p>
                    <p className="text-xs text-gray-500">Legal representation</p>
                  </div>
                  <p className="text-sm text-gray-900">$2,000-$5,000</p>
                </div>
                <div className="flex justify-between items-start pb-3 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Title Insurance</p>
                    <p className="text-xs text-gray-500">Owner's policy (if required)</p>
                  </div>
                  <p className="text-sm text-gray-900">$1,000-$3,000</p>
                </div>
                <div className="flex justify-between items-start pb-3 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Transfer Taxes</p>
                    <p className="text-xs text-gray-500">State and local taxes</p>
                  </div>
                  <p className="text-sm text-gray-900">0.5-2%</p>
                </div>
                <div className="flex justify-between items-start pb-3 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Recording Fees</p>
                    <p className="text-xs text-gray-500">Document recording</p>
                  </div>
                  <p className="text-sm text-gray-900">$200-$500</p>
                </div>
                <div className="flex justify-between items-start pb-3 border-b border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Payoff Fees</p>
                    <p className="text-xs text-gray-500">Existing loan payoff costs</p>
                  </div>
                  <p className="text-sm text-gray-900">Varies</p>
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Miscellaneous Fees</p>
                    <p className="text-xs text-gray-500">Courier, wire transfer, etc.</p>
                  </div>
                  <p className="text-sm text-gray-900">$500-$1,000</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-calculator-line text-green-600 mr-2"></i>
                Common Prorations and Credits
              </h4>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Property Taxes</h5>
                  <p className="text-gray-600 text-xs">
                    Prorated based on closing date. If you've prepaid taxes beyond closing, you receive a 
                    credit. If taxes are unpaid, the amount is deducted from your proceeds. Verify tax 
                    calculations carefully as errors are common.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Rent Collections</h5>
                  <p className="text-gray-600 text-xs">
                    Rent collected for periods after closing belongs to the buyer. If you've collected rent 
                    beyond the closing date, you credit the buyer. Conversely, if rent is unpaid, buyers 
                    typically assume responsibility for collection.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Security Deposits</h5>
                  <p className="text-gray-600 text-xs">
                    All tenant security deposits transfer to the buyer at closing. You must provide detailed 
                    accounting and transfer the actual funds. This is not a credit against purchase price but 
                    an actual cash transfer.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Utilities and Services</h5>
                  <p className="text-gray-600 text-xs">
                    Prepaid utilities, trash service, pest control, and other services are prorated. Provide 
                    documentation of prepaid amounts to receive proper credits. Cancel or transfer services 
                    effective on closing date.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Operating Expenses</h5>
                  <p className="text-gray-600 text-xs">
                    Insurance premiums, management fees, and other operating expenses are prorated to closing 
                    date. Prepaid expenses result in credits to you; unpaid expenses are deducted from proceeds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=property%20manager%20conducting%20final%20walkthrough%20inspection%20of%20apartment%20building%20with%20new%20owner%20checking%20systems%20and%20conditions%20clipboard%20documentation%20smooth%20ownership%20transition%20professional%20handover&width=800&height=600&seq=tips-closing-002&orientation=landscape"
              alt="Final walkthrough inspection"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Final Walkthrough and Closing Day</h3>
            <p className="text-gray-600 mb-6">
              The final walkthrough and closing day are the last steps in selling your apartment building. 
              Being prepared for these final events is among the most practical tips for selling an apartment 
              building and ensures everything concludes smoothly without last-minute surprises or complications.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-eye-line text-orange-500 mr-2"></i>
                  Final Walkthrough
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  Buyers typically conduct a final walkthrough 24-48 hours before closing to verify property 
                  condition hasn't changed since their inspections. Ensure the property is clean, all agreed 
                  repairs are complete, and nothing has been removed that should convey with the property.
                </p>
                <ul className="text-gray-600 text-xs space-y-1">
                  <li>• Verify all systems are operational</li>
                  <li>• Ensure agreed repairs are completed</li>
                  <li>• Confirm nothing has been removed</li>
                  <li>• Address any last-minute concerns immediately</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-file-edit-line text-green-500 mr-2"></i>
                  Closing Day Procedures
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  Closing typically occurs at the title company or attorney's office. You'll sign numerous 
                  documents transferring ownership and receive your proceeds. The process usually takes 1-2 
                  hours. Bring valid identification and be prepared to sign many documents.
                </p>
                <ul className="text-gray-600 text-xs space-y-1">
                  <li>• Review all documents before signing</li>
                  <li>• Ask questions about anything unclear</li>
                  <li>• Verify wire transfer instructions for proceeds</li>
                  <li>• Obtain copies of all signed documents</li>
                  <li>• Transfer all keys, codes, and materials to buyer</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-checkbox-circle-line text-red-500 mr-2"></i>
                  Post-Closing Tasks
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  After closing, complete these final tasks to ensure a clean break and avoid post-closing 
                  complications or liability.
                </p>
                <ul className="text-gray-600 text-xs space-y-1">
                  <li>• Notify tenants of ownership change in writing</li>
                  <li>• Cancel insurance policies effective closing date</li>
                  <li>• Forward any mail or tenant payments received</li>
                  <li>• Provide any additional information buyer requests</li>
                  <li>• Keep copies of all closing documents</li>
                  <li>• Consult with tax advisor about reporting requirements</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}